import { useState, useEffect } from 'react';

type TeamColor = 'BLUE' | 'RED' | 'GREEN' | 'YELLOW';

interface AudienceMember {
  nickname: string;
  connected: boolean;
}

interface AudienceByTeam {
  BLUE: AudienceMember[];
  RED: AudienceMember[];
  GREEN: AudienceMember[];
  YELLOW: AudienceMember[];
}

const TEAM_COLORS: Record<TeamColor, { bg: string; text: string; border: string }> = {
  BLUE: { bg: 'bg-blue-900/30', text: 'text-blue-300', border: 'border-blue-600' },
  RED: { bg: 'bg-red-900/30', text: 'text-red-300', border: 'border-red-600' },
  GREEN: { bg: 'bg-green-900/30', text: 'text-green-300', border: 'border-green-600' },
  YELLOW: { bg: 'bg-yellow-900/30', text: 'text-yellow-300', border: 'border-yellow-600' },
};

const TEAM_NAMES: Record<TeamColor, string> = {
  BLUE: 'Blu',
  RED: 'Rossa',
  GREEN: 'Verde',
  YELLOW: 'Gialla',
};

interface ConnectedPlayersCollapsibleProps {
  sessionId: string;
  numPlayers: 2 | 3 | 4;
  refreshInterval?: number;
}

export function ConnectedPlayersCollapsible({
  sessionId,
  numPlayers,
  refreshInterval = 5000,
}: ConnectedPlayersCollapsibleProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [audience, setAudience] = useState<AudienceByTeam | null>(null);
  const [total, setTotal] = useState(0);
  const [connected, setConnected] = useState(0);

  const activeTeams: TeamColor[] = numPlayers === 2
    ? ['BLUE', 'RED']
    : numPlayers === 3
    ? ['BLUE', 'RED', 'GREEN']
    : ['BLUE', 'RED', 'GREEN', 'YELLOW'];

  useEffect(() => {
    fetchAudience();
    const interval = setInterval(fetchAudience, refreshInterval);
    return () => clearInterval(interval);
  }, [sessionId, refreshInterval]);

  const fetchAudience = async () => {
    try {
      const response = await fetch(`/api/sessions/${sessionId}/audience`);
      if (response.ok) {
        const data = await response.json();
        setAudience(data.audience);
        setTotal(data.total);
        setConnected(data.connected);
      }
    } catch (error) {
      console.error('[ConnectedPlayers] Error fetching audience:', error);
    }
  };

  return (
    <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
      {/* Header (Always Visible) */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-4 flex items-center justify-between hover:bg-gray-750 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="text-xl">{isExpanded ? '▼' : '▶'}</span>
          <h3 className="text-lg font-bold text-white">Giocatori Connessi</h3>
          <span className="text-gray-400 text-sm">
            ({connected} online / {total} totali)
          </span>
        </div>
        <span className="text-gray-500 text-sm">
          {isExpanded ? 'Clicca per chiudere' : 'Clicca per espandere'}
        </span>
      </button>

      {/* Collapsible Content */}
      {isExpanded && audience && (
        <div className="p-4 border-t border-gray-700">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {activeTeams.map((team) => {
              const colors = TEAM_COLORS[team];
              const members = audience[team] || [];
              const connectedCount = members.filter((m) => m.connected).length;

              return (
                <div
                  key={team}
                  className={`${colors.bg} rounded-lg p-3 border ${colors.border}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className={`font-semibold ${colors.text}`}>
                      {TEAM_NAMES[team]}
                    </span>
                    <span className="text-gray-400 text-xs">
                      {connectedCount}/{members.length}
                    </span>
                  </div>

                  {members.length === 0 ? (
                    <p className="text-gray-500 text-xs italic">Nessun giocatore</p>
                  ) : (
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {members.map((member, idx) => (
                        <div
                          key={idx}
                          className="flex items-center gap-2 text-xs"
                        >
                          <span
                            className={`w-2 h-2 rounded-full ${
                              member.connected ? 'bg-green-500' : 'bg-gray-600'
                            }`}
                          />
                          <span
                            className={
                              member.connected ? 'text-white' : 'text-gray-500'
                            }
                          >
                            {member.nickname}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
